import { FC } from "react";
import { Link } from "react-router-dom";

import { useTime } from "@/app/application/TimeContext";
import { formatDateTimeSession } from "@/features/training/lib/trainingDateUtils";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { Box, Chip, Paper, Typography, alpha, useTheme } from "@mui/material";

export type SessionStatus = "active" | "archived" | "complete";

interface SessionListItemProps {
	link: string;
	/** Primary display title (preferred) */
	title?: string;
	/** Legacy: use as title when title not provided */
	name?: string;
	date: Date | string;
	status?: SessionStatus;
	/** Optional stats line e.g. "12 participants · 11 pass · 1 fail" */
	secondaryText?: string;
	/** Legacy: alternating row style (ignored in new design) */
	isEvenListItemStyling?: boolean;
}

const statusConfig: Record<
	SessionStatus,
	{
		label: string;
		color: "success" | "warning" | "default";
		icon: React.ReactNode;
	}
> = {
	active: {
		label: "Active",
		color: "warning",
		icon: <ScheduleIcon sx={{ fontSize: 14 }} />,
	},
	archived: {
		label: "Archived",
		color: "default",
		icon: <HistoryEduIcon sx={{ fontSize: 14 }} />,
	},
	complete: {
		label: "Completed",
		color: "success",
		icon: <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />,
	},
};

export const SessionListItem: FC<SessionListItemProps> = ({
	link,
	title,
	name,
	date,
	status = "active",
	secondaryText,
}) => {
	const displayTitle = title ?? name ?? "Session";
	const theme = useTheme();
	const { timezone } = useTime();
	const sc = statusConfig[status];
	const formattedDate = formatDateTimeSession(date, timezone);

	return (
		<Link to={link} style={{ textDecoration: "none" }}>
			<Paper
				elevation={0}
				role="button"
				aria-label={`${displayTitle} — ${formattedDate}`}
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1.5,
					px: 2,
					py: 1.5,
					mb: 1,
					borderRadius: 2,
					border: `1px solid ${theme.palette.divider}`,
					bgcolor: "background.paper",
					transition: "background-color 0.2s ease, border-color 0.2s ease",
					"&:hover": {
						bgcolor: alpha(theme.palette.primary.main, 0.04),
						borderColor: alpha(theme.palette.primary.main, 0.22),
					},
					"&:active": {
						transform: "translateY(0.5px)",
					},
					"&:focus-within": {
						outline: "none",
						boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.12)}`,
					},
				}}
			>
				<Box
					sx={{
						width: 40,
						height: 40,
						borderRadius: 2,
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
						bgcolor: alpha(theme.palette.primary.main, 0.06),
						color: "primary.main",
					}}
				>
					<HistoryEduIcon sx={{ fontSize: 20 }} />
				</Box>

				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography
						variant="subtitle1"
						fontWeight={600}
						sx={{ color: "text.primary", lineHeight: 1.3 }}
						noWrap
					>
						{displayTitle}
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
						{formattedDate}
					</Typography>
					{secondaryText && (
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ mt: 0.25, display: "block" }}
						>
							{secondaryText}
						</Typography>
					)}
				</Box>

				{status && (
					<Chip
						size="small"
						icon={sc.icon as React.ReactElement}
						label={sc.label}
						color={sc.color}
						variant="outlined"
						sx={{ fontWeight: 500 }}
					/>
				)}

				<KeyboardArrowRightIcon
					fontSize="small"
					sx={{ color: "text.disabled", ml: 0.5 }}
				/>
			</Paper>
		</Link>
	);
};
